import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import documentoService from '../services/documentoService';
import api from '../services/api';
import * as XLSX from 'xlsx-js-style';
import { PERMISOS, PERMISOS_MODULOS } from '../utils/permissions';
import { fechaHoy, fechaHoyMasDias, fechaHoyMasMeses } from '../utils/dateUtils';
import '../styles/ConsultaDocumentos.css';
import EditarDocumentoModal from '../components/EditarDocumentoModal';
import { LuPencil, LuFileText, LuChartColumn, LuFileImage, LuTrash2, LuFileSpreadsheet, LuFileDigit, LuCalendarDays, LuRectangleEllipsis, LuChurch, LuFileType, LuUsers, LuPin, LuSearch, LuFileSearch } from "react-icons/lu";

const ConsultaDocumentos = () => {
  const { usuario } = useAuth();
  const toast = useToast();
  
  // Determinar filtros iniciales según permisos
  const getFiltrosIniciales = () => {
    const baseFilters = {
      tipoRegistro: 'Todos',
      fechaInicio: fechaHoyMasMeses(-1),
      // Un día después de hoy para incluir documentos recién registrados
      fechaFin: fechaHoyMasDias(1),
      estado: 'AC',
      motivo: '',
      tipoDoc: '',
      numDoc: '',
      glosa: '',        
      local: '',       
      grupo: '',        
      usuario: ''      


    };

    // Miembro (1) y Digitador (2): Solo su usuario, local y grupo
    if (usuario.Permiso <= PERMISOS.DIGITADOR) {
      return {
        ...baseFilters,
        usuario: usuario.NombreUsu,
        local: usuario.Local,
        grupo: usuario.GrupoId // ← Cambiar a grupo y GrupoId
      };
    }
    
    // Jefe de Grupo (3): Puede ver todos los grupos de su local
    if (usuario.Permiso === PERMISOS.JEFE_GRUPO) {
      return {
        ...baseFilters,
        usuario: '',
        local: usuario.Local,
        grupo: ''
      };
    }

    // Administrador (4) y Pastor (5): Todo su local
    if (usuario.Permiso >= PERMISOS.ADMINISTRADOR && usuario.Permiso < PERMISOS.TESORERO) {
      return {
        ...baseFilters,
        usuario: '',
        local: usuario.Local,
        grupo: '' // ← Cambiar a grupo
      };
    }

    // Tesorero (6): Todo su local
    if (usuario.Permiso === PERMISOS.TESORERO) {
      return {
        ...baseFilters,
        usuario: '',
        local: usuario.Local,
        grupo: '' // ← Cambiar a grupo
      };
    }

    // Zona (7), Distrito (8), Programador (9): Todo
    return {
      ...baseFilters,
      usuario: '',
      local: '',
      grupo: '' // ← Cambiar a grupo
    };
  };

  const [filtros, setFiltros] = useState(getFiltrosIniciales());
  const [documentos, setDocumentos] = useState([]);
  const [totales, setTotales] = useState({
    ingresos: 0,
    gastos: 0,
    saldo: 0
  });
  const [catalogos, setCatalogos] = useState({
    usuarios: [],
    locales: [],
    grupos: [], // ← Cambiar a grupos
    motivos: [],
    tiposDoc: [],
    estados: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [documentoAEditar, setDocumentoAEditar] = useState(null);
  const [mostrarModalEditar, setMostrarModalEditar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busquedaTexto, setBusquedaTexto] = useState('');
  // Ordenamiento por columna (server-side). campo vacío = orden por defecto (Fecha DESC)
  const [orden, setOrden] = useState({ campo: '', direccion: 'DESC' });

  // Determinar qué filtros están deshabilitados según permisos
  const filtrosDeshabilitados = {
    usuario: usuario.Permiso <= PERMISOS.DIGITADOR,
    local: usuario.Permiso < PERMISOS.ZONA,
    grupo: usuario.Permiso < PERMISOS.JEFE_GRUPO // Solo Miembro y Digitador no pueden cambiar grupo
  };

  // Cargar catálogos al montar
  useEffect(() => {
    cargarCatalogos();
    if (PERMISOS_MODULOS.PUEDE_VER_TODOS_USUARIOS(usuario)) {
      cargarUsuarios();
    }
  }, []);
  

  // Cargar documentos cuando cambien la página, el límite o el ordenamiento
  useEffect(() => {
    consultarDocumentos();
  }, [pagination.page, pagination.limit, orden]);

  const cargarCatalogos = async () => {
    try {
      const response = await api.get('/catalogos');
      if (response.data.success) {
        let locales = response.data.data.locales;
        let grupos = response.data.data.grupos; // ← Cambiar a grupos

        // Filtrar locales según permisos
        if (usuario.Permiso < PERMISOS.ZONA) {
          locales = locales.filter(local => local.CodLocal === usuario.Local);
        }

        // Filtrar grupos según permisos
        if (usuario.Permiso < PERMISOS.ADMINISTRADOR) {
          grupos = grupos.filter(grupo => grupo.id === usuario.GrupoId); // ← Usar id
        }

        setCatalogos({
          locales,
          grupos, // ← Cambiar a grupos
          motivos: response.data.data.motivos,
          tiposDoc: response.data.data.tiposDoc,
          estados: response.data.data.estados,
          usuarios: []
        });
      }
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };
  

  const cargarUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      if (response.data.success) {
        let usuarios = response.data.data;

        // Filtrar usuarios según permisos
        if (usuario.Permiso <= PERMISOS.DIGITADOR) {
          usuarios = usuarios.filter(u => u.NombreUsu === usuario.NombreUsu);
        } else if (usuario.Permiso === PERMISOS.JEFE_GRUPO) {
          usuarios = usuarios.filter(u => u.GrupoId === usuario.GrupoId); // ← Usar GrupoId
        } else if (usuario.Permiso >= PERMISOS.ADMINISTRADOR && usuario.Permiso < PERMISOS.ZONA) {
          usuarios = usuarios.filter(u => u.Local === usuario.Local);
        }

        setCatalogos(prev => ({
          ...prev,
          usuarios: usuarios
        }));
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    }
  };

  const consultarDocumentos = async () => {
    setLoading(true);
    try {
      const filtrosParaEnviar = {
        ...filtros,
        page: pagination.page,
        limit: pagination.limit,
        orderBy: orden.campo,
        orderDir: orden.direccion
      };

      // Eliminar filtros vacíos
      Object.keys(filtrosParaEnviar).forEach(key => {
      const value = filtrosParaEnviar[key];
      if (value === '' || value === 'Todos' || value === null || value === undefined) {
        delete filtrosParaEnviar[key];
      }
    });
      console.log('📤 Filtros enviados desde frontend:', filtrosParaEnviar);

      const [responseDocumentos, responseTotales] = await Promise.all([
        documentoService.obtenerDocumentos(filtrosParaEnviar),
        documentoService.obtenerTotales(filtrosParaEnviar)
      ]);

      if (responseDocumentos.success) {
        setDocumentos(responseDocumentos.data.documentos);
        setPagination(prev => ({
          ...prev,
          total: responseDocumentos.data.pagination.total,
          totalPages: responseDocumentos.data.pagination.totalPages
        }));
      }

      if (responseTotales.success) {
        setTotales(responseTotales.data);
      }
    } catch (error) {
      console.error('Error al consultar documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    if (name === 'fechaInicio') {
      if (filtros.fechaFin && value > filtros.fechaFin) {
        toast.warning('La fecha "Desde" no puede ser mayor que la fecha "Hasta"');
        return;
        }
      }

    if (name === 'fechaFin') {
      if (filtros.fechaInicio && value < filtros.fechaInicio) {
        toast.warning('La fecha "Hasta" no puede ser menor que la fecha "Desde"');
        return;
      }
    }
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTipoRegistroChange = (tipo) => {
    setFiltros(prev => ({
      ...prev,
      tipoRegistro: tipo
    }));
  };

  const handleConsultar = () => {
  // Validar fechas antes de consultar
  if (filtros.fechaInicio && filtros.fechaFin) {
    if (filtros.fechaInicio > filtros.fechaFin) {
      toast.warning('La fecha "Desde" no puede ser mayor que la fecha "Hasta"');
      return;
    }
  }
  
  setPagination(prev => ({ ...prev, page: 1 }));
  consultarDocumentos();
};

  const handleLimpiarFiltros = () => {
    setFiltros(getFiltrosIniciales());
    setBusquedaTexto('');
    setOrden({ campo: '', direccion: 'DESC' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Ordenar la tabla por una columna. Alterna ASC/DESC y vuelve a la página 1.
  const handleOrdenar = (campo) => {
    setOrden(prev => ({
      campo,
      direccion: prev.campo === campo && prev.direccion === 'ASC' ? 'DESC' : 'ASC'
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Indicador visual de la dirección de orden en el encabezado
  const renderOrdenIcono = (campo) => {
    if (orden.campo !== campo) return ' ⇅';
    return orden.direccion === 'ASC' ? ' ↑' : ' ↓';
  };

  const handleExportar = async () => {
  if (!PERMISOS_MODULOS.PUEDE_EXPORTAR(usuario)) {
    toast.error('Sin permisos para exportar. Se requiere nivel Jefe de Grupo o superior.');
    return;
  }

  try {
    setLoading(true);

    // Traer TODOS los documentos que cumplan los filtros actuales (no solo la
    // página visible). Se usa el total conocido como límite; si aún no se ha
    // consultado, se usa un límite alto para cubrir todos los resultados.
    const filtrosParaEnviar = {
      ...filtros,
      page: 1,
      limit: pagination.total || 100000,
      orderBy: orden.campo,
      orderDir: orden.direccion
    };

    Object.keys(filtrosParaEnviar).forEach(key => {
      const value = filtrosParaEnviar[key];
      if (value === '' || value === 'Todos' || value === null || value === undefined) {
        delete filtrosParaEnviar[key];
      }
    });

    const responseDocumentos = await documentoService.obtenerDocumentos(filtrosParaEnviar);
    const todosLosDocumentos = responseDocumentos.success
      ? responseDocumentos.data.documentos
      : [];

    const documentosVisibles = filtrarPorBusqueda(todosLosDocumentos);

    if (documentosVisibles.length === 0) {
      toast.warning('No hay documentos para exportar');
      return;
    }

    const datosExcel = documentosVisibles.map(doc => ({
      'ID': doc.id,
      'Fecha': formatFecha(doc.Fecha),
      'Hora': doc.Hora,
      'Número Documento': doc.NumDoc,
      'Tipo Documento': doc.tipoDocInfo?.Nombre || '',
      'Monto': doc.Monto,
      'Local': doc.localInfo?.Nombre || '',
      'Centro de Costo': doc.CentroCosto || '',
      'Glosa': doc.Glosa || '',
      'Usuario': doc.usuarioInfo?.NombreUsu || '',
      'Tipo de Registro': doc.TipoRegistro || '',
      'Estado': doc.estadoInfo?.Nombre || '',
      'Motivo': doc.motivoInfo?.NombreMotivo || doc.Motivo,
    }));

    // Agregar totales
    datosExcel.push({});
    datosExcel.push({
      'ID': 'TOTALES',
      'Tipo Documento': 'Ingresos',
      'Monto': totales.ingresos
    });
    datosExcel.push({
      'Tipo Documento': 'Gastos',
      'Monto': totales.gastos
    });
    datosExcel.push({
      'Tipo Documento': 'Saldo',
      'Monto': totales.saldo
    });

    // Crear hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(datosExcel);
    
    // ========================================
    // DEFINIR ESTILOS
    // ========================================
    
    // Estilo para encabezados
    const headerStyle = {
      fill: { fgColor: { rgb: "4472C4" } },
      font: { color: { rgb: "FFFFFF" }, bold: true, sz: 12 },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };
    
    // Estilo para celdas de Ingreso
    const ingresoStyle = {
      fill: { fgColor: { rgb: "C6EFCE" } },
      font: { color: { rgb: "006100" }, bold: true },
      alignment: { horizontal: "center", vertical: "center" }
    };
    
    // Estilo para celdas de Gasto
    const gastoStyle = {
      fill: { fgColor: { rgb: "FFC7CE" } },
      font: { color: { rgb: "9C0006" }, bold: true },
      alignment: { horizontal: "center", vertical: "center" }
    };
    
    // Estilo para totales
    const totalStyle = {
      fill: { fgColor: { rgb: "F2F2F2" } },
      font: { bold: true, sz: 11 },
      alignment: { horizontal: "right", vertical: "center" }
    };
    
    // Estilo normal para celdas
    const normalStyle = {
      alignment: { vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "D3D3D3" } },
        bottom: { style: "thin", color: { rgb: "D3D3D3" } },
        left: { style: "thin", color: { rgb: "D3D3D3" } },
        right: { style: "thin", color: { rgb: "D3D3D3" } }
      }
    };
    
    // ========================================
    // APLICAR ESTILOS
    // ========================================
    
    const range = XLSX.utils.decode_range(ws['!ref']);
    
    // Aplicar estilo a encabezados (fila 0)
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (ws[cellAddress]) {
        ws[cellAddress].s = headerStyle;
      }
    }
    
    // Aplicar estilos a datos
    for (let row = 1; row <= range.e.r; row++) {
      // Obtener el valor de "Tipo de Registro" (columna K, índice 10)
      const tipoRegistroAddress = XLSX.utils.encode_cell({ r: row, c: 10 });
      const tipoRegistro = ws[tipoRegistroAddress]?.v;
      
      // Aplicar estilos a cada celda de la fila
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        
        if (!ws[cellAddress]) continue;
        
        // Verificar si es fila de totales
        const idCell = ws[XLSX.utils.encode_cell({ r: row, c: 0 })];
        if (idCell?.v === 'TOTALES' || idCell?.v === undefined) {
          ws[cellAddress].s = totalStyle;
          continue;
        }
        
        // Aplicar estilo según el tipo de registro
        if (col === 10) { // Columna "Tipo de Registro"
          if (tipoRegistro === 'Ingreso') {
            ws[cellAddress].s = ingresoStyle;
          } else if (tipoRegistro === 'Gasto') {
            ws[cellAddress].s = gastoStyle;
          } else {
            ws[cellAddress].s = normalStyle;
          }
        } else if (col === 5) { // Columna "Monto"
          if (tipoRegistro === 'Ingreso') {
            ws[cellAddress].s = {
              ...ingresoStyle,
              numFmt: '"$"#,##0.00' // Formato de moneda
            };
          } else if (tipoRegistro === 'Gasto') {
            ws[cellAddress].s = {
              ...gastoStyle,
              numFmt: '"$"#,##0.00' // Formato de moneda
            };
          } else {
            ws[cellAddress].s = {
              ...normalStyle,
              numFmt: '"$"#,##0.00'
            };
          }
        } else {
          ws[cellAddress].s = normalStyle;
        }
      }
    }
    
    // ========================================
    // CREAR ARCHIVO
    // ========================================
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Documentos');

    // Anchos de columna
    ws['!cols'] = [
      { wch: 8 },  // ID
      { wch: 12 }, // Fecha
      { wch: 10 }, // Hora
      { wch: 20 }, // Número Documento
      { wch: 20 }, // Tipo Documento
      { wch: 15 }, // Monto
      { wch: 20 }, // Local
      { wch: 20 }, // Centro de Costo
      { wch: 35 }, // Glosa
      { wch: 15 }, // Usuario
      { wch: 18 }, // Tipo de Registro
      { wch: 12 }, // Estado
      { wch: 20 }  // Motivo
    ];

    const fecha = fechaHoy();
    const nombreArchivo = `Documentos_${fecha}.xlsx`;

    XLSX.writeFile(wb, nombreArchivo);
    
    toast.success(`Archivo ${nombreArchivo} descargado exitosamente`);
  } catch (error) {
    console.error('Error al exportar:', error);
    toast.error(`Error al exportar: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  const handleVerImagen = (ruta) => {
  if (ruta) {
    const url = documentoService.getImagenUrl(ruta);
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

  const formatMonto = (monto) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(monto);
  };

  const formatFecha = (fecha) => {
    return new Date(fecha + 'T00:00:00').toLocaleDateString('es-CL');
  };

  const filtrarPorBusqueda = (docs) => {
    if (!busquedaTexto) return docs;
    
    const texto = busquedaTexto.toLowerCase();
    return docs.filter(doc => 
      doc.Glosa?.toLowerCase().includes(texto) ||
      doc.usuarioInfo?.Nombre?.toLowerCase().includes(texto) ||
      doc.motivoInfo?.NombreMotivo?.toLowerCase().includes(texto) ||
      doc.Monto.toString().includes(texto) ||
      doc.NumDoc.toString().includes(texto)
    );
  };

  const documentosFiltrados = filtrarPorBusqueda(documentos);

  const handleEditarDocumento = (documento) => {
  if (!PERMISOS_MODULOS.PUEDE_EDITAR_DOCUMENTOS(usuario)) {
    toast.error('Sin permisos para editar documentos.');
    return;
  }
  if (documento.Estado === 'AN') {
    toast.warning('No se puede editar un documento anulado');
    return;
  }
  setDocumentoAEditar(documento);
  setMostrarModalEditar(true);
};

  const handleCerrarModal = () => {
    setMostrarModalEditar(false);
    setDocumentoAEditar(null);
  };

  const handleEdicionExitosa = () => {
    consultarDocumentos();
    setMostrarModalEditar(false);
    setDocumentoAEditar(null);
  };
  return (
    <div className="consulta-container">
      <div className="consulta-header">
        <h2>Consulta de Documentos</h2>
        {usuario.Permiso <= PERMISOS.DIGITADOR && (
          <p className="restriccion-info">
            ℹ️ Visualizando solo documentos de tu usuario y local
          </p>
        )}
        {usuario.Permiso === PERMISOS.JEFE_GRUPO && (
          <p className="restriccion-info">
            ℹ️ Visualizando solo documentos de tu grupo
          </p>
        )}
        {usuario.Permiso >= PERMISOS.ADMINISTRADOR && usuario.Permiso < PERMISOS.ZONA && (
          <p className="restriccion-info">
            ℹ️ Visualizando solo documentos de tu local
          </p>
        )}
      </div>

      {/* Filtros */}
      <div className="filtros-card">
        {/* Tipo de registro */}
        <div className="filtro-section">
          <label className="filtro-label">
            <i className="icon"><LuFileText/></i> Tipo de registro
          </label>
          <div className="tipo-registro-buttons">
            <button
              type="button"
              className={`tipo-btn ${filtros.tipoRegistro === 'Todos' ? 'active todos' : ''}`}
              onClick={() => handleTipoRegistroChange('Todos')}
            >
              <span className="radio-icon">{filtros.tipoRegistro === 'Todos' ? '⦿' : '○'}</span>
              Todos
            </button>
            <button
              type="button"
              className={`tipo-btn ${filtros.tipoRegistro === 'Ingreso' ? 'active ingreso' : ''}`}
              onClick={() => handleTipoRegistroChange('Ingreso')}
            >
              <span className="radio-icon">{filtros.tipoRegistro === 'Ingreso' ? '⦿' : '○'}</span>
              Ingreso
            </button>
            <button
              type="button"
              className={`tipo-btn ${filtros.tipoRegistro === 'Gasto' ? 'active gasto' : ''}`}
              onClick={() => handleTipoRegistroChange('Gasto')}
            >
              <span className="radio-icon">{filtros.tipoRegistro === 'Gasto' ? '⦿' : '○'}</span>
              Gastos
            </button>
          </div>
        </div>

        {/* Fechas */}
        <div className="filtro-section">
          <label className="filtro-label">
            <i className="icon"><LuCalendarDays/></i> Fecha de búsqueda
          </label>
          <div className="fecha-row">
            <div className="fecha-group">
              <label htmlFor="fechaInicio">Desde</label>
              <input
                type="date"
                id="fechaInicio"
                name="fechaInicio"
                value={filtros.fechaInicio}
                max={filtros.fechaFin || undefined}
                onChange={handleFiltroChange}
              />
            </div>
            <div className="fecha-group">
              <label htmlFor="fechaFin">Hasta</label>
              <input
                type="date"
                id="fechaFin"
                name="fechaFin"
                value={filtros.fechaFin}
                min={filtros.fechaInicio || undefined}
                onChange={handleFiltroChange}
              />
            </div>
          </div>
        </div>

        {/* Fila 1: Usuario y Estado */}
        <div className="filtros-row">
          <div className="filtro-group">
            <label htmlFor="usuario">
              <i className="icon"><LuUsers/></i> Usuario
              {filtrosDeshabilitados.usuario && <span className="badge-bloqueado">🔒</span>}
            </label>
            <select
              id="usuario"
              name="usuario"
              value={filtros.usuario || ''}
              onChange={handleFiltroChange}
              disabled={filtrosDeshabilitados.usuario}
            >
              <option value="">Todos los usuarios</option>
              {catalogos.usuarios.map(user => (
                <option key={user.id} value={user.NombreUsu}>
                  {user.NombreUsu} - {user.Nombre} {user.ApellidoPat}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-group">
            <label htmlFor="estado">
              <i className="icon"><LuChartColumn /></i> Estado
            </label>
            <select
              id="estado"
              name="estado"
              value={filtros.estado}
              onChange={handleFiltroChange}
            >
              <option value="">Todos</option>
              {catalogos.estados.map(estado => (
                <option key={estado.id} value={estado.CodEstado}>
                  {estado.Nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Fila 2: Local y Grupo */}
        <div className="filtros-row">
          <div className="filtro-group">
            <label htmlFor="local">
              <i className="icon"><LuChurch/></i> Iglesia
              {filtrosDeshabilitados.local && <span className="badge-bloqueado">🔒</span>}
            </label>
            <select
              id="local"
              name="local"
              value={filtros.local}
              onChange={handleFiltroChange}
              disabled={filtrosDeshabilitados.local}
            >
              <option value="">Todos</option>
              {catalogos.locales.map(local => (
                <option key={local.id} value={local.CodLocal}>
                  {local.Nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-group">
            <label htmlFor="grupo">
              <i className="icon"><LuUsers/></i> Grupo
              {filtrosDeshabilitados.grupo && <span className="badge-bloqueado">🔒</span>}
            </label>
            <select
              id="grupo"
              name="grupo"
              value={filtros.grupo}
              onChange={handleFiltroChange}
              disabled={filtrosDeshabilitados.grupo}
            >
              <option value="">Todos los grupos</option>
              {catalogos.grupos.map(grupo => (
                <option key={grupo.id} value={grupo.id}>
                  {grupo.Descripcion || grupo.Nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Fila 3: Motivo y Tipo Documento */}
        <div className="filtros-row">
          <div className="filtro-group">
            <label htmlFor="motivo">
              <i className="icon"><LuPin/></i> Motivo
            </label>
            <select
              id="motivo"
              name="motivo"
              value={filtros.motivo}
              onChange={handleFiltroChange}
            >
              <option value="">Todos los motivos</option>
              {catalogos.motivos.map(motivo => (
                <option key={motivo.Id} value={motivo.CodMotivo}>
                  {motivo.NombreMotivo}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-group">
            <label htmlFor="tipoDoc">
              <i className="icon"><LuFileType/></i> Tipo de documento
            </label>
            <select
              id="tipoDoc"
              name="tipoDoc"
              value={filtros.tipoDoc}
              onChange={handleFiltroChange}
            >
              <option value="">Todos los documentos</option>
              {catalogos.tiposDoc.map(tipo => (
                <option key={tipo.id} value={tipo.CodDoc}>
                  {tipo.Nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Fila 4: Número y Glosa */}
        <div className="filtros-row">
          <div className="filtro-group">
            <label htmlFor="numDoc">
              <i className="icon"><LuFileDigit/></i> Número
            </label>
            <input
              type="number"
              id="numDoc"
              name="numDoc"
              value={filtros.numDoc}
              onChange={handleFiltroChange}
              placeholder="Número de documento"
            />
          </div>

          <div className="filtro-group">
            <label htmlFor="glosa">
              <i className="icon"><LuRectangleEllipsis/></i> Glosa
            </label>
            <input
              type="text"
              id="glosa"
              name="glosa"
              value={filtros.glosa}
              onChange={handleFiltroChange}
              placeholder="Buscar en glosa"
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="filtros-actions">
          <button className="btn-consultar" onClick={handleConsultar} disabled={loading}>
            <i className="icon"><LuSearch/></i>
            {loading ? 'Consultando...' : 'Consultar'}
          </button>
          <button className="btn-limpiar" onClick={handleLimpiarFiltros}>
            <i className="icon"><LuTrash2 /></i>
            Limpiar Filtros
          </button>
          {PERMISOS_MODULOS.PUEDE_EXPORTAR(usuario) && (
            <button className="btn-exportar" onClick={handleExportar}>
              <i className="icon"><LuFileSpreadsheet /></i>
              Exportar a Excel
            </button>
          )}
        </div>
      </div>

      {/* Totales */}
      <div className="totales-card">
        <div className="total-item ingreso">
          <span className="total-label">Total Ingresos</span>
          <span className="total-valor">{formatMonto(totales.ingresos)}</span>
        </div>
        <div className="total-item gasto">
          <span className="total-label">Total Gastos</span>
          <span className="total-valor">{formatMonto(totales.gastos)}</span>
        </div>
        <div className="total-item saldo">
          <span className="total-label">Saldo</span>
          <span className="total-valor">{formatMonto(totales.saldo)}</span>
        </div>
      </div>

      {/* Buscador de tabla */}
      <div className="tabla-header">
        <div className="tabla-info">
          Mostrando <strong>{documentosFiltrados.length}</strong> de <strong>{pagination.total}</strong> registros
        </div>
        <div className="tabla-buscar">
          <input
            type="text"
            placeholder="Buscar en tabla..."
            value={busquedaTexto}
            onChange={(e) => setBusquedaTexto(e.target.value)}
          />
        </div>
      </div>

      {/* Tabla de documentos */}
<div className="tabla-container">
  <div className="tabla-scroll-wrapper">
    <table className="documentos-table">
      <thead>
        <tr>
          <th className="ordenable" onClick={() => handleOrdenar('id')}>Id{renderOrdenIcono('id')}</th>
          <th className="ordenable" onClick={() => handleOrdenar('monto')}>Monto{renderOrdenIcono('monto')}</th>
          <th className="ordenable" onClick={() => handleOrdenar('fecha')}>Fecha{renderOrdenIcono('fecha')}</th>
          <th className="ordenable" onClick={() => handleOrdenar('motivo')}>Motivo{renderOrdenIcono('motivo')}</th>
          <th className="ordenable" onClick={() => handleOrdenar('grupo')}>Grupo{renderOrdenIcono('grupo')}</th>
          <th>Glosa o Motivo de anulación</th>
          <th className="ordenable" onClick={() => handleOrdenar('usuario')}>Usuario{renderOrdenIcono('usuario')}</th>
          <th className="ordenable" onClick={() => handleOrdenar('local')}>Iglesia{renderOrdenIcono('local')}</th>
          <th className="ordenable" onClick={() => handleOrdenar('tipoDoc')}>Tipo Documento{renderOrdenIcono('tipoDoc')}</th>
          <th className="ordenable" onClick={() => handleOrdenar('numDoc')}>Num Documento{renderOrdenIcono('numDoc')}</th>
          <th className="ordenable" onClick={() => handleOrdenar('estado')}>Estado{renderOrdenIcono('estado')}</th>
          <th>Imágenes</th>
          <th className="ordenable" onClick={() => handleOrdenar('glosa')}>Glosa{renderOrdenIcono('glosa')}</th>
          {PERMISOS_MODULOS.PUEDE_EDITAR_DOCUMENTOS(usuario) ? (
          <th>Acción</th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan="12" className="loading-cell">
              Cargando documentos...
            </td>
          </tr>
        ) : documentosFiltrados.length === 0 ? (
          <tr>
            <td colSpan="14" className="empty-cell">
              <div className="empty-state">
                <LuFileSearch className="empty-state-icon" />
                <p className="empty-state-title">Sin resultados</p>
                <p className="empty-state-desc">No se encontraron documentos con los filtros aplicados</p>
              </div>
            </td>
          </tr>
        ) : (
          documentosFiltrados.map((doc) => (
            <tr key={doc.id} className={doc.Estado === 'AN' ? 'anulado' : ''}>
              <td>
                {doc.id}
              </td>
              <td className="monto-cell">
                <span className={doc.TipoRegistro === 'Ingreso' ? 'monto-ingreso' : 'monto-gasto'}>
                  {formatMonto(doc.Monto)}
                </span>
              </td>
              <td>{formatFecha(doc.Fecha)} {doc.Hora}</td>
              <td>{doc.motivoInfo?.NombreMotivo || doc.Motivo}</td>
              <td>{doc.grupoInfo?.Descripcion || doc.grupoInfo?.Nombre}</td>
              <td className="glosa-cell">
                {doc.Estado === 'AN' ? doc.MotivoAnulacion : doc.Glosa}
              </td>
              <td>{doc.usuarioInfo?.Nombre} {doc.usuarioInfo.ApellidoPat}</td>
              <td>{doc.localInfo?.Nombre}</td>
              <td>{doc.tipoDocInfo?.Nombre}</td>
              <td>{doc.NumDoc}</td>
              <td>
                <span className={`badge-estado ${doc.Estado === 'AC' ? 'activo' : 'anulado'}`}>
                  {doc.estadoInfo?.Nombre}
                </span>
              </td>
              <td className="imagenes-cell">
                {doc.imagenes && doc.imagenes.length > 0 ? (
                  <button 
                    className="btn-ver-imagen"
                    onClick={() => handleVerImagen(doc.imagenes[0].ruta)}
                  >
                    <i className="icon"><LuFileImage/></i> Ver Imagen
                  </button>
                ) : (
                  <span className="sin-archivos">Sin archivos adjuntos</span>
                )}
              </td>
              <td className="glosa-cell">{doc.Glosa}</td>
              <td className="acciones-cell">
                {PERMISOS_MODULOS.PUEDE_EDITAR_DOCUMENTOS(usuario) ? (
                  <button
                    className="btn-editar-doc"
                    onClick={() => handleEditarDocumento(doc)}
                    disabled={doc.Estado === 'AN'}
                    title={doc.Estado === 'AN' ? 'No se puede editar documento anulado' : 'Editar'}
                  >
                    <LuPencil />
                  </button>
                ) : (
                  <span className="sin-permisos">-</span>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
</div>

      {/* Paginación */}
      <div className="paginacion">
        <div className="paginacion-info">
          Página {pagination.page} de {pagination.totalPages}
        </div>
        <div className="paginacion-controles">
          <select
            value={pagination.limit}
            onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <span>registros</span>
        </div>
        <div className="paginacion-botones">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
            disabled={pagination.page === 1}
          >
            ««
          </button>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
          >
            ‹
          </button>
          <span className="pagina-actual">{pagination.page}</span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.totalPages}
          >
            ›
          </button>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
            disabled={pagination.page === pagination.totalPages}
          >
            »»
          </button>
        </div>
      </div>
      {mostrarModalEditar && documentoAEditar && (
        <EditarDocumentoModal
          documento={documentoAEditar}
          onClose={handleCerrarModal}
          onSuccess={handleEdicionExitosa}
        />
)}
    </div>
  );
};

export default ConsultaDocumentos;